import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { tokens } from '@hotjar/hotsauce';
import { CollectionsSelector } from '@hotjar/webapp/features/Collections';
import { RemovalConfirmation } from '@hotjar/comments/components/RemovalConfirmation';
import { Thread } from '@hotjar/comments/components/Thread';
import { useComments } from '@hotjar/comments/hooks/useComments';
import { LabelsContext } from '@hotjar/webapp/contexts/labels';
import { LabelsEditor } from '@hotjar/labels';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';

import type { Highlight } from '@hotjar/webapp/domains/highlights/model';
import { invalidateQuery } from '@hotjar/webapp/features/Highlights/util';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { HighlightPopoverContainer } from './HighlightPopoverContainer';
import { PopoverHeaderText } from './PopoverHeaderText';
import {
  HEATMAP_HIGHLIGHT_VIEWED,
  RECORDING_HIGHLIGHT_VIEWED,
} from './SingleHighlight/use-view-tracking';
import { useHighlightCollections } from './use-highlight-collections';
import { useHighlightLabels } from './use-highlight-labels';
import { ClickStatsDialog } from './ClickStatistics/ClickStatsDialog';
import { LabelsMultiSelect } from './LabelsMultiSelect';

const RECORDING_HIGHLIGHT_ADDED_TO_COLLECTION = 'recording_highlight_added_to_collection';

const HEATMAP_HIGHLIGHT_ADDED_TO_COLLECTION = 'heatmap_highlight_added_to_collection';

const PopoverContainer = styled(HighlightPopoverContainer)`
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};
  overflow: hidden;
`;

const NewThreadWrapper = styled.div`
  margin-bottom: ${tokens.spacer.x2};
`;

const StyledClickStatsDialog = styled(ClickStatsDialog)`
  margin-bottom: ${tokens.spacer.x2};
`;

const CollectionsContainer = styled.div`
  background-color: ${tokens.color.bg_active};
  margin: -24px;
  margin-top: 0px;
  padding: 24px;
  border-top: 1px solid ${tokens.color.ui_border};
`;

type CollectionId = string;

interface ExistingHighlightPopoverProps {
  apiToken?: Parameters<typeof useComments>[0]['apiToken'];
  currentUser: Parameters<typeof useComments>[0]['currentUser'];
  deactivateCurrentItem: () => void;
  deactivateNotInteractedCurrentItem?: () => void;
  isPlaying?: boolean;
  item: Highlight;
  markCurrentItemAsInteracted?: () => void;
  onPause?: Parameters<typeof useComments>[0]['onPause'];
  onShare?: (startTime: number) => void;
  organizationId: Parameters<typeof useComments>[0]['organizationId'];
  refetchHighlights?: () => void;
  setActiveItem?: Parameters<typeof useComments>[0]['setActiveItem'];
  siteId: string | null;
  isDesktop: boolean;
}

export function ExistingHighlightPopover({
  apiToken = null,
  currentUser,
  deactivateCurrentItem,
  isPlaying = false,
  item,
  onPause = () => null,
  onShare = () => null,
  organizationId,
  refetchHighlights = () => null,
  setActiveItem = () => null,
  siteId,
  isDesktop = true,
}: ExistingHighlightPopoverProps): JSX.Element | null {
  const isCustomLabelsAllowed = useFeatureFlag('settings.custom-labels');
  const { areThreadsFetched, threads, threadProps, removalConfirmationProps, renderNewThread } =
    useComments({
      apiToken,
      autoFocusTextField: false,
      compactNewThread: true,
      currentUser,
      isPlaying,
      newThreadMetadata: {},
      onPause,
      onThreadAdded: () => refetchHighlights(),
      onThreadRemoved: () => {
        refetchHighlights();
        deactivateCurrentItem();
      },
      organizationId,
      scopeId: item.commentId ? Number(item.commentId) : null,
      setActiveItem,
      siteId,
      widgetId: Number(item.id),
      widgetType: 'highlight',
      invalidateQuery,
    });

  const { t } = useTranslation();
  const { add: addLabel, remove: removeLabel } = useHighlightLabels(siteId, String(item.id));
  const { add: addCollection, remove: removeCollection } = useHighlightCollections(
    siteId,
    String(item.id),
  );
  const [selectedLabelIds, setSelectedLabelIds] = useState<Set<string>>(
    new Set(item.labels.map((label) => label.id)),
  );

  const [selectedCollectionIds, setSelectedCollectionIds] = useState<Set<CollectionId>>(
    new Set(item.collections.map((collection) => collection.id)),
  );
  const shouldRender = areThreadsFetched;

  const handleEventSend = (): void => {
    event(
      item.type === 'recording'
        ? RECORDING_HIGHLIGHT_ADDED_TO_COLLECTION
        : HEATMAP_HIGHLIGHT_ADDED_TO_COLLECTION,
    );
  };

  useEffect(() => {
    if (shouldRender) {
      event(item.type === 'recording' ? RECORDING_HIGHLIGHT_VIEWED : HEATMAP_HIGHLIGHT_VIEWED);
    }
  }, [shouldRender, item.type]);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <PopoverContainer
        currentUser={{ id: String(currentUser.id) }}
        highlight={{
          ...item,
          id: String(item.id),
        }}
        onShare={onShare}
        onClose={deactivateCurrentItem}
        siteId={String(siteId)}
      >
        {item.type === 'heatmap' && (
          <StyledClickStatsDialog
            clicksCount={item.metadata.clicksCount ?? 0}
            clicksPercentage={item.metadata.clicksPercentage ?? 0}
            isDesktop={isDesktop}
          />
        )}
        {!isCustomLabelsAllowed && (
          <>
            <PopoverHeaderText as="p" textType="subtitle2">
              {t('highlights:recordings.collections')}
            </PopoverHeaderText>

            <CollectionsSelector
              onAdd={addCollection}
              onEventSend={handleEventSend}
              onRemove={removeCollection}
              onSelectedIdsChange={setSelectedCollectionIds}
              selectedCollectionIds={selectedCollectionIds}
              siteId={String(siteId)}
            />
          </>
        )}
        <PopoverHeaderText as="p" textType="subtitle2">
          {t('highlights:recordings.labels')}
        </PopoverHeaderText>
        {!isCustomLabelsAllowed && (
          <LabelsContext>
            <LabelsEditor
              onAdd={addLabel}
              onRemove={removeLabel}
              onSelectedIdsChange={setSelectedLabelIds}
              selectedLabelIds={selectedLabelIds}
              siteId={String(siteId)}
            />
          </LabelsContext>
        )}

        {isCustomLabelsAllowed && (
          <LabelsMultiSelect siteId={siteId} itemId={item.id} itemLabels={item.labels} />
        )}

        <PopoverHeaderText as="p" textType="subtitle2">
          {t('highlights:recordings.comments')}
        </PopoverHeaderText>
        {threads.length === 0 ? (
          <NewThreadWrapper>{renderNewThread()}</NewThreadWrapper>
        ) : (
          <Thread {...threadProps} onClose={deactivateCurrentItem} thread={threads[0]} />
        )}
        {isCustomLabelsAllowed && (
          <CollectionsContainer>
            <PopoverHeaderText as="p" textType="subtitle2">
              {t('highlights:recordings.collections')}
            </PopoverHeaderText>

            <CollectionsSelector
              onAdd={addCollection}
              onEventSend={handleEventSend}
              onRemove={removeCollection}
              onSelectedIdsChange={setSelectedCollectionIds}
              selectedCollectionIds={selectedCollectionIds}
              siteId={String(siteId)}
            />
          </CollectionsContainer>
        )}
      </PopoverContainer>
      <RemovalConfirmation {...removalConfirmationProps} />
    </>
  );
}
